import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import {
  Select,
  Input,
  Text,
  ButtonCircle,
  Message,
} from 'rebass';
import { Flex, Box } from 'reflexbox';
import Loader from 'react-loading';

import {
  MAX_LEVEL,
  classes,
  races,
  isTank,
  isHealer,
  isUnsupportedSpec,
} from 'shared/character';
import SimcInput from 'shared/simcinput';
import SharedConfig from 'shared/config';
import { isTitanDiscBelt } from 'shared/items';

import config from 'frontend/config';
import {
  Icon,
  If,
  ReactSelect,
} from 'frontend/components';
import TalentStrip from 'frontend/components/Simbot/TalentStrip';
import GearStrip from 'frontend/components/Simbot/GearStrip';
import { DEVMODE, isBetaSite } from 'frontend/util';

import actions from 'frontend/state/actions';
import { selectors as simbotSelectors } from 'frontend/state/reducers/simbot';
import { selectors as realmsSelectors } from 'frontend/state/reducers/simbot/realms';
import { selectors as formSelectors } from 'frontend/state/reducers/simbot/form';
import { selectors as characterSelectors } from 'frontend/state/reducers/simbot/character';
import { selectors as siteSelectors } from 'frontend/state/reducers/site';

const MAX_CHARACTER_DATA_AGE = 30 * 60 * 1000;

class ArmoryInput extends React.Component {
  static propTypes = {
    armoryRegion: PropTypes.string.isRequired,
    armoryRealm: PropTypes.string.isRequired,
    armoryName: PropTypes.string.isRequired,

    character: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    characterLoading: PropTypes.bool,
    characterError: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    characterLoadTime: PropTypes.number,

    gear: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,

    realms: PropTypes.arrayOf(PropTypes.object).isRequired,

    showUnsupportedWarning: PropTypes.bool,
    showGear: PropTypes.bool,

    fetchChar: PropTypes.func.isRequired,
    fetchRealms: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
  };

  static defaultProps = {
    character: null,
    characterLoading: false,
    characterError: null,
    characterLoadTime: null,
    showUnsupportedWarning: true,
    showGear: true,
  };

  componentDidMount() {
    if (this.isDisabled()) {
      return;
    }
    if (!this.props.realms || this.props.realms.length === 0) {
      this.props.fetchRealms();
    }
    if (!this.props.character) {
      this.props.fetchChar();
    } else if (this.props.characterLoadTime) {
      // check for stale character data
      const dataAge = (new Date().getTime()) - this.props.characterLoadTime;
      if (dataAge > MAX_CHARACTER_DATA_AGE) {
        this.props.fetchChar();
      }
    }
  }

  isDisabled = () => {
    if (DEVMODE) {
      return false;
    }
    if (isBetaSite()) {
      return !SharedConfig.beta.features.armory;
    }
    return !SharedConfig.features.armory;
  };


  render() {
    const {
      armoryRegion,
      armoryRealm,
      armoryName,
      character,
      characterLoading,
      characterError,
      gear,
      realms,
      showUnsupportedWarning,
      showGear,
      onFieldChange,
    } = this.props;

    if (this.isDisabled()) {
      return (
        <div>Armory lookup is not currently available</div>
      );
    }

    const activeLoadout = _.find(character?.talentLoadouts, { active: true });
    const specName = activeLoadout?.talents?.specName;

    const hasTitanBeltEquipped = isTitanDiscBelt(gear?.waist);

    return (
      <div>
        <Flex wrap col={12} gutter={1}>
          <Box md={1} sm={5} ml={1} style={{ minWidth: '70px' }}>
            <If
              condition={realms}
              then={() => (
                <Select
                  id="ArmoryInput-armoryRegion"
                  label="Region"
                  name="region"
                  value={armoryRegion}
                  options={_.map(
                    _.uniq(_.map(realms, 'origin')),
                    region => ({
                      value: region,
                      children: region.toUpperCase(),
                    }),
                  )}
                  onChange={e => onFieldChange('armoryRegion', e.target.value)}
                  color={config.color}
                  backgroundColor={config.colors.primary}
                />
              )}
            />
          </Box>
          <Box md={2} sm={7} ml={1} style={{ minWidth: '200px' }}>
            <If
              condition={realms}
              then={() => {
                const options = _.map(
                  _.filter(realms, { origin: armoryRegion }),
                  realm => ({
                    value: realm.slug,
                    label: realm.name,
                  }),
                );
                const value = _.find(options, { value: armoryRealm });
                return (
                  <ReactSelect
                    id="ArmoryInput-armoryRealm"
                    label="Realm"
                    name="realm"
                    value={value}
                    options={options}
                    isClearable
                    blurInputOnSelect
                    onChange={realm => onFieldChange('armoryRealm', _.get(realm, 'value') || null)}
                  />
                );
              }}
            />
          </Box>
          <Box md={8} sm={12} ml={1}>
            <Flex align="center" wrap>
              <Box pr={1}>
                <Input
                  id="ArmoryInput-armorySearch"
                  type="text"
                  name="armorySearch"
                  role="search"
                  data-lpignore="true"
                  label="Character"
                  value={armoryName}
                  onChange={e => onFieldChange('armoryName', e.target.value)}
                  autoComplete="new-username"
                  color={config.color}
                  backgroundColor={config.colors.primary}
                />
              </Box>
              <Box mb={0} mt={1} ml={1}>
                {
                  characterLoading
                    ? (
                      <Loader
                        type="spin"
                        color={config.colors.green}
                        height={20}
                        width={20}
                        delay={100}
                      />
                    ) :
                    null
                }
                <If
                  condition={characterError}
                  then={() => (
                    <Flex align="center" style={{ height: 24 }}>
                      <Box m={0}>
                        <If
                          condition={characterError.status === 404}
                          then={() => (
                            <ButtonCircle
                              mr={1}
                              backgroundColor={config.colors.error}
                              size={20}
                              style={{
                                minHeight: 20,
                                width: 20,
                                lineHeight: 20,
                              }}
                            >
                              <Icon name="close" size={15} style={{ marginRight: 1 }} />
                            </ButtonCircle>
                          )}
                          else={() => (
                            <ButtonCircle
                              id="ArmoryInput-refreshCharacter"
                              mr={1}
                              backgroundColor={config.colors.warning}
                              size={20}
                              style={{
                                minHeight: '20px',
                                lineHeight: '20px',
                              }}
                            >
                              <Icon
                                name="refresh"
                                color={config.colors.black}
                                size={14}
                                onClick={this.props.fetchChar}
                              />
                            </ButtonCircle>
                          )}
                        />
                      </Box>
                      <Box>
                        <Text bold color={config.colors.error}>
                          {characterError.reason}
                        </Text>
                      </Box>
                    </Flex>
                  )}
                />

                <If
                  condition={character}
                  then={() => (
                    <Flex align="center">
                      <Box>
                        <ButtonCircle
                          mr={1}
                          backgroundColor={config.colors.success}
                          size={20}
                          style={{
                            minHeight: '20px',
                            lineHeight: '20px',
                          }}
                          onClick={this.props.fetchChar}
                        >
                          <Icon
                            name="refresh"
                            color={config.colors.black}
                            size={14}
                          />
                        </ButtonCircle>
                      </Box>
                      <Box>
                        <Box>
                          {character.level}
                          {' '}
                          {_.get(_.find(races, { id: character.race }), 'name', '')}
                          {' '}
                          {specName}
                          {' '}
                          {_.get(_.find(classes, { id: character.class }), 'name')}
                        </Box>
                      </Box>
                    </Flex>
                  )}
                />
              </Box>
            </Flex>
          </Box>
        </Flex>
        <If
          condition={character && showGear}
          then={() => (
            <Flex align="center" mb={1}>
              <Box mb={0} style={{ height: 18 }}>
                {character && gear && (
                <GearStrip
                  size="small"
                  equippedGear={gear}
                  classId={character.class}
                  specName={specName}
                  showEmptySlots
                />
                )}
              </Box>
              <If
                condition={character.level === MAX_LEVEL}
                then={() => (
                  <Box ml={2}>
                    <Text
                      id="ArmoryInput-convertToSimcInput"
                      small
                      color={config.colors.orange}
                      style={{
                        cursor: 'pointer',
                        height: 18,
                      }}
                      onClick={() => {
                        const input = SimcInput.stringify(SimcInput.fromCharacter(character, armoryRegion));
                        onFieldChange('text', input);
                      }}
                    >
                      Convert to SimC input
                    </Text>
                  </Box>
                )}
              />
            </Flex>
          )}
        />
        <Box mb={1}>
          <TalentStrip
            talents={activeLoadout?.talents}
            level={character?.level}
            specId={activeLoadout?.talents?.specId}
            size={20}
          />
        </Box>
        <If
          condition={character && isUnsupportedSpec(character) && showUnsupportedWarning}
          then={() => (
            <Message theme="error" p={1} mt={1} mb={0}>
              Your spec is not supported by Raidbots and/or SimulationCraft
            </Message>
          )}
          else={() => (
            <If
              condition={isTank(character) || isHealer(character)}
              then={() => (
                <Message p={1} mb={0} theme="warning" mt={1}>
                  Tanks and Healers sim DPS, not survival or healing
                </Message>
              )}
            />
          )}
        />
        <If
          condition={characterError}
          then={() => (
            <Message p={1} theme="warning">
              Use the SimC addon if problems persist. The Armory is often flaky.
            </Message>
          )}
        />
        <If
          condition={character && hasTitanBeltEquipped}
          then={() => (
            <Message p={1} mt={1} theme="warning">
              Durable Information Securing Container (11.1.7 Belt) uses the default Charged Bolts when loading from
              Armory.
              &nbsp;
              <a
                href="https://support.raidbots.com/article/70-armory-and-disc-belt-limitations"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: config.colors.white, textDecoration: 'underline' }}
              >
                More info
              </a>
            </Message>
          )}
        />
      </div>
    );
  }
}

const mapStateToProps = state => state;

const mapDispatchToProps = dispatch => ({ dispatch });

const mergeProps = (state, dispatchProps, ownProps) => {
  const stateProps = {
    armoryName: formSelectors.armoryName(state),
    armoryRegion: formSelectors.armoryRegion(state),
    armoryRealm: formSelectors.armoryRealm(state),
    characterError: characterSelectors.error(state),
    characterLoading: characterSelectors.loading(state),
    character: characterSelectors.data(state),
    characterLoadTime: characterSelectors.when(state),
    talents: characterSelectors.talents(state),
    gear: simbotSelectors.equippedGear(state),
    realms: realmsSelectors.realms(state),
    isBetaSite: siteSelectors.isBetaSite(state),
  };
  const { dispatch } = dispatchProps;
  return {
    ...ownProps,
    ...stateProps,
    onFieldChange: (field, value) => dispatch(actions.inputForm(field, value)),
    fetchRealms: () => actions.fetchRealms()(dispatch),
    fetchChar: () => actions.fetchChar(dispatch, () => state),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ArmoryInput);
